import React, { forwardRef } from 'react';
import {
  Input as InputCu,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  ResponsiveValue,
  useBreakpointValue,
  Box,
} from '@chakra-ui/react';
import { ForwardRefComponent } from 'framer-motion';
import { Text1 } from '../../Typography/Text1';
import { Icon } from '../../Icon/Icon';
import { InputPropsType } from '../Form.types';
import { OmitProps } from '../../utils/OmitProps';

// Dimensioni delle icone per ogni size
const sizesIcon = {
  xs: 'inputXs',
  sm: 'inputSm',
  md: 'inputMd',
  lg: 'inputLg',
};

// Dimensioni degli elementi per ogni size
const elementSizes = {
  xs: 6,
  sm: 8,
  md: 10,
  lg: 12,
};

// Funzione helper per determinare il colore in base allo stato
const getStateColor = ({
  hasError,
  isValid,
  isPopulated,
  customColor,
  isDisabled,
}: {
  hasError: boolean;
  isValid: boolean;
  isPopulated: boolean;
  customColor?: string;
  isDisabled?: boolean;
}): string => {
  if (customColor) return customColor;
  if (hasError) return 'palette.errors.50';
  if (isValid) return 'palette.success.50';
  if (isPopulated) return 'palette.primary.50';
  if (isDisabled) return 'palette.text.secondary.90';
  return 'palette.primary.80';
};

const InputWithOmittedProps = OmitProps(InputCu, ['fieldState', 'formState']);

export const Input: ForwardRefComponent<InputPropsType & InputProps, any> =
  forwardRef(
    (
      {
        size = 'md',
        customRightIcon,
        customLeftIcon,
        errors,
        rules,
        value,
        onChange,
        name,
        fieldState,
        isInvalid,
        helperText,
        ...props
      },
      ref,
    ) => {
      const hasError = !!errors || isInvalid;
      const isValid = !!rules && fieldState?.isDirty && !hasError && !!value;
      const isPopulated = !!value && !isValid && !hasError;

      // Gestione responsive delle dimensioni
      const currentSize =
        useBreakpointValue(typeof size === 'object' ? size : { base: size }) ||
        'md';

      const iconSize = sizesIcon[currentSize as keyof typeof sizesIcon];
      const elementSize =
        elementSizes[currentSize as keyof typeof elementSizes];

      // Uso elementSize per tutti i valori che devono essere responsive
      const leftPadding = elementSize;
      const rightPadding = elementSize;
      const responsiveElementSize = elementSize;

      // Funzione helper per renderizzare le icone
      const renderIcon = (icon: any) => {
        if (typeof icon === 'string') {
          const color = getStateColor({
            hasError,
            isValid,
            isPopulated,
            customColor: props.color,
            isDisabled: props.isDisabled,
          });
          return <Icon icon={icon} size={iconSize} color={color} />;
        }
        return icon;
      };

      return (
        <>
          <InputGroup size={currentSize}>
            {customLeftIcon && (
              <InputLeftElement
                h={responsiveElementSize}
                w={responsiveElementSize}
              >
                {renderIcon(customLeftIcon)}
              </InputLeftElement>
            )}

            <InputWithOmittedProps
              ref={ref}
              id={name}
              onChange={onChange}
              value={value || ''}
              isInvalid={hasError}
              pl={customLeftIcon ? leftPadding : undefined}
              pr={rightPadding}
              {...props}
              size={currentSize as ResponsiveValue<string>}
            />

            <InputRightElement
              h={responsiveElementSize}
              w={responsiveElementSize}
            >
              {/* Linea divisoria condizionale */}
              {(customRightIcon || isValid || hasError) && (
                <Box
                  position="absolute"
                  left={0}
                  top="50%"
                  transform="translateY(-50%)"
                  width="1px"
                  height="60%"
                  bg={getStateColor({
                    hasError,
                    isValid,
                    isPopulated: false,
                    customColor: props.color,
                    isDisabled: props.isDisabled,
                  })}
                  _groupHover={{
                    bg: hasError
                      ? 'palette.errors.50'
                      : isValid
                        ? 'palette.success.50'
                        : 'palette.primary.50',
                  }}
                  _groupFocusWithin={{
                    bg: hasError
                      ? 'palette.errors.50'
                      : isValid
                        ? 'palette.success.50'
                        : 'palette.primary.50',
                  }}
                  _groupDisabled={{
                    bg: 'palette.secondary.90',
                  }}
                />
              )}

              {isValid && (
                <Icon
                  icon="IcPositive"
                  width={iconSize}
                  height={iconSize}
                  color="palette.success.50"
                />
              )}
              {customRightIcon &&
                !isValid &&
                !hasError &&
                renderIcon(customRightIcon)}
              {hasError && (
                <Icon
                  icon="IcDraft"
                  size={iconSize}
                  color="palette.errors.50"
                />
              )}
            </InputRightElement>
          </InputGroup>
          {helperText && !hasError && (
            <Text1
              mt={1}
              fontSize={currentSize}
              color="palette.text.secondary.50"
            >
              {helperText}
            </Text1>
          )}
        </>
      );
    },
  );
